import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Download, Search, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ActionPopover } from '@/components/action-popover'
import { LoadingButton } from '@/components/loading-button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  type SpecialPromotion,
  type SpecialsImportResult,
  type SpecialsListParams,
  useDownloadSpecialsTemplate,
  useEndSpecial,
  useImportSpecials,
  useSpecialProducts,
  useSpecials,
} from '@/api/hooks'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatMoney } from '@/lib/utils'
import { specialMinimum, specialTerm } from '@/features/specials/format'
import { SearchInput } from '@/components/search-input'
import { getApiErrorMessage } from '@/lib/api-error'

type StatusFilter = NonNullable<SpecialsListParams['status']>

const PAGE_SIZE = 50
// Skips the list asked for or that cannot change a price; the preview shows only the rest.
const EXPECTED_SKIPS = new Set([
  'excluded line',
  'no current price',
  'no current net',
  'special is not lower than our net',
  'has a Special NET (no stacking)',
])

function parseDay(value: string): Date {
  const [y, m, d] = value.split('-').map(Number)
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
}

function formatDates(startsOn: string, endsOn: string): string {
  const start = parseDay(startsOn)
  const end = parseDay(endsOn)
  const month = (date: Date) => date.toLocaleDateString('en-US', { month: 'short' })
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${month(start)} ${start.getDate()}–${end.getDate()}`
  }
  return `${month(start)} ${start.getDate()} – ${month(end)} ${end.getDate()}`
}

function coverage(special: SpecialPromotion): string {
  if (special.whole_line) return 'Whole line'
  return `${special.product_count.toLocaleString()} ${special.product_count === 1 ? 'product' : 'products'}`
}

const STATUS_LABEL: Record<SpecialPromotion['status'], string> = {
  active: 'Active',
  upcoming: 'Upcoming',
  ended: 'Ended',
}

export function SpecialsPage() {
  const [status, setStatus] = useState<StatusFilter>('active')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<SpecialPromotion | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const debouncedSearch = useDebouncedValue(search)
  const template = useDownloadSpecialsTemplate()

  const { data, isLoading, isError } = useSpecials({
    status,
    search: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })
  const items = data?.items ?? []
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))
  const counts = data?.counts ?? {}
  const showStatus = status === 'all'

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Specials</h1>
        <div className="flex flex-wrap gap-2">
          <LoadingButton
            variant="outline"
            loading={template.isPending}
            onClick={() =>
              template.mutate(undefined, { onError: (error) => toast.error(getApiErrorMessage(error, 'Template download failed')) })
            }
          >
            <Download className="h-4 w-4" /> Template
          </LoadingButton>
          <Button onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" /> Load list
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <Tabs
          value={status}
          onValueChange={(value) => {
            setStatus(value as StatusFilter)
            setPage(1)
          }}
        >
          <TabsList>
            {(['active', 'upcoming', 'ended'] as const).map((value) => (
              <TabsTrigger key={value} value={value}>
                {STATUS_LABEL[value]}
                <span className="ml-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                  {counts[value] ?? 0}
                </span>
              </TabsTrigger>
            ))}
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search vendor, promotion or program"
            aria-label="Search specials"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
      </div>

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Vendor</TableHead>
              <TableHead>Promotion</TableHead>
              <TableHead>Special</TableHead>
              <TableHead>Minimum</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead className="text-right">Covers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-destructive">
                  Specials could not be loaded. Refresh to try again.
                </TableCell>
              </TableRow>
            ) : items.length ? (
              items.map((special) => (
                <TableRow
                  key={special.key}
                  className="cursor-pointer"
                  onClick={() => setSelected(special)}
                >
                  <TableCell className="min-w-[11rem] whitespace-normal">
                    <Link
                      to="/manufacturers/$manuId"
                      params={{ manuId: String(special.manufacturer.uuid) }}
                      className="font-medium text-primary hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {special.manufacturer.name}
                    </Link>
                  </TableCell>
                  <TableCell className="min-w-[14rem] whitespace-normal">
                    <button
                      type="button"
                      className="text-left hover:underline focus-visible:underline focus-visible:outline-none"
                      onClick={(event) => {
                        event.stopPropagation()
                        setSelected(special)
                      }}
                    >
                      {special.promotion ?? special.program}
                    </button>
                  </TableCell>
                  <TableCell className="min-w-[8rem] max-w-[16rem] whitespace-normal font-medium">{specialTerm(special)}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {specialMinimum(special) ?? '-'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="font-mono text-xs tabular-nums">
                      {formatDates(special.starts_on, special.ends_on)}
                    </span>
                    {showStatus && (
                      <Badge variant={special.status === 'active' ? 'default' : 'secondary'} className="ml-2">
                        {STATUS_LABEL[special.status]}
                      </Badge>
                    )}
                    {special.on_hold && (
                      <Badge variant="secondary" className="ml-2">
                        On hold
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-muted-foreground">
                    {special.perk && !special.extra_discount && special.net_price == null
                      ? '-'
                      : coverage(special)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No specials
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-2 md:hidden">
        {isLoading ? (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : items.length ? (
          items.map((special) => {
            const minimum = specialMinimum(special)
            return (
              <button
                key={special.key}
                type="button"
                onClick={() => setSelected(special)}
                className="block w-full rounded-lg border bg-card p-3 text-left transition-colors hover:bg-muted/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 flex-1 break-words text-sm font-medium">
                    {special.manufacturer.name}
                  </p>
                  <span className="max-w-[55%] shrink-0 text-right text-sm font-medium">{specialTerm(special)}</span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{special.promotion ?? special.program}</p>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="font-mono tabular-nums">{formatDates(special.starts_on, special.ends_on)}</span>
                  {minimum && <span>{minimum}</span>}
                  {showStatus && <span>{STATUS_LABEL[special.status]}</span>}
                  {special.on_hold && <span>On hold</span>}
                </div>
              </button>
            )
          })
        ) : (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            No specials
          </div>
        )}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {pages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <SpecialSheet special={selected} onClose={() => setSelected(null)} />
      <ImportSpecialsDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}

function SpecialSheet({ special, onClose }: { special: SpecialPromotion | null; onClose: () => void }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebouncedValue(search)
  const endSpecial = useEndSpecial()
  const products = useSpecialProducts(special?.key ?? null, {
    search: debouncedSearch || undefined,
    offset: (page - 1) * 25,
  })
  const total = products.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / 25))
  const priced = special != null && (special.extra_discount != null || special.net_price != null)

  const close = () => {
    setSearch('')
    setPage(1)
    onClose()
  }

  const end = () => {
    if (!special) return
    const upcoming = special.status === 'upcoming'
    endSpecial.mutate(special.key, {
      onSuccess: () => {
        toast.success(upcoming ? 'Special removed' : 'Special ended')
        close()
      },
      onError: () => toast.error('The special could not be ended'),
    })
  }

  const facts: [string, string | null | undefined][] = special
    ? [
        ['Special', specialTerm(special)],
        ['Minimum', specialMinimum(special)],
        ['Dates', `${formatDates(special.starts_on, special.ends_on)}, ${parseDay(special.ends_on).getFullYear()}`],
        ['Program', special.program],
        ['PO marking', special.po_note],
      ]
    : []

  return (
    <Sheet open={special != null} onOpenChange={(open) => !open && close()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        {special && (
          <>
            <SheetHeader className="border-b px-4 py-4 sm:px-6">
              <SheetTitle>
                <Link
                  to="/manufacturers/$manuId"
                  params={{ manuId: String(special.manufacturer.uuid) }}
                  className="text-primary hover:underline"
                >
                  {special.manufacturer.name}
                </Link>
              </SheetTitle>
              <p className="text-sm">{special.promotion ?? special.program}</p>
            </SheetHeader>
            <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4 sm:px-6">
              <dl className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2 text-sm">
                {facts
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label} className="contents">
                      <dt className="text-muted-foreground">{label}</dt>
                      <dd className="font-medium">{value}</dd>
                    </div>
                  ))}
              </dl>
              {special.terms_note && (
                <section className="space-y-1">
                  <h2 className="text-sm font-semibold">Vendor terms</h2>
                  <p className="text-sm text-muted-foreground">{special.terms_note}</p>
                </section>
              )}
              {priced && (
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold">
                      {special.whole_line ? 'Whole line' : 'Products'}
                      <span className="ml-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                        {total.toLocaleString()}
                      </span>
                    </h2>
                    <SearchInput
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      placeholder="Search model"
                      aria-label="Search products"
                      className="h-8 max-w-[12rem]"
                    />
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Model</TableHead>
                        <TableHead className="hidden text-right sm:table-cell">List</TableHead>
                        <TableHead className="text-right">NET today</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.isLoading ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : products.data?.items.length ? (
                        products.data.items.map((product) => (
                          <TableRow key={String(product.uuid)}>
                            <TableCell className="max-w-[16rem] whitespace-normal">
                              <Link
                                to="/products/$productId"
                                params={{ productId: String(product.uuid) }}
                                className="font-medium text-primary hover:underline"
                              >
                                {product.model}
                              </Link>
                              {product.title && (
                                <p className="truncate text-xs text-muted-foreground">{product.title}</p>
                              )}
                            </TableCell>
                            <TableCell className="hidden text-right font-mono text-xs tabular-nums sm:table-cell">
                              {formatMoney(product.list_price)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs tabular-nums">
                              {formatMoney(product.net_price)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            No products
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  {pages > 1 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Page {page} of {pages}
                      </span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page >= pages}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </section>
              )}
              {special.source_reference && (
                <p className="text-xs text-muted-foreground">{special.source_reference}</p>
              )}
            </div>
            {special.status !== 'ended' && (
              <SheetFooter className="border-t px-4 py-3 sm:px-6">
                <ActionPopover
                  align="end"
                  trigger={
                    <LoadingButton
                      variant="outline"
                      className="text-destructive"
                      loading={endSpecial.isPending}
                      loadingText={special.status === 'upcoming' ? 'Removing...' : 'Ending...'}
                    >
                      {special.status === 'upcoming' ? 'Remove' : 'End now'}
                    </LoadingButton>
                  }
                  title={
                    special.status === 'upcoming'
                      ? `Remove ${special.manufacturer.name}: ${special.promotion ?? special.program}?`
                      : `End ${special.manufacturer.name}: ${special.promotion ?? special.program} today?`
                  }
                  confirmLabel={special.status === 'upcoming' ? 'Remove' : 'End now'}
                  confirmVariant="destructive"
                  onConfirm={(_note, closeConfirm) => {
                    closeConfirm()
                    end()
                  }}
                >
                  {special.status !== 'upcoming' && (
                    <p className="text-xs text-muted-foreground">Prices return to regular terms.</p>
                  )}
                </ActionPopover>
              </SheetFooter>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function ImportSpecialsDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<SpecialsImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const importSpecials = useImportSpecials()

  const reset = () => {
    setFile(null)
    setResult(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const choose = (chosen: File | null) => {
    setFile(chosen)
    setResult(null)
    setError(null)
    if (!chosen) return
    importSpecials.mutate(
      { file: chosen, apply: false },
      { onSuccess: setResult, onError: (failure) => setError(failure.message) },
    )
  }

  const apply = () => {
    if (!file) return
    importSpecials.mutate(
      { file, apply: true },
      {
        onSuccess: (applied) => {
          toast.success(`Loaded ${applied.programs.join(', ')}`)
          reset()
          onOpenChange(false)
        },
        onError: (failure) => setError(failure.message),
      },
    )
  }

  const issues = (result?.lines ?? [])
    .map((line) => ({
      ...line,
      skipped: Object.fromEntries(
        Object.entries(line.skipped).filter(([reason]) => !EXPECTED_SKIPS.has(reason)),
      ),
    }))
    .filter(
      (line) => line.products === 0 || Object.keys(line.skipped).length > 0 || line.missing_models.length > 0,
    )

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Load specials list</DialogTitle>
        </DialogHeader>
        <Input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          aria-label="Specials list CSV"
          onChange={(event) => choose(event.target.files?.[0] ?? null)}
        />
        {importSpecials.isPending && !result && (
          <p className="text-sm text-muted-foreground" role="status">
            Checking the list against the catalog...
          </p>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        {result && (
          <div className="space-y-4">
            <dl className="grid grid-cols-3 gap-3 rounded-lg border p-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Promotions</dt>
                <dd className="font-mono text-base font-semibold tabular-nums">{result.rows}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Products</dt>
                <dd className="font-mono text-base font-semibold tabular-nums">
                  {result.products.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Match nothing</dt>
                <dd className="font-mono text-base font-semibold tabular-nums">{result.unmatched_rows}</dd>
              </div>
            </dl>
            <p className="text-sm">
              Replaces every special in <span className="font-medium">{result.programs.join(', ')}</span>.
            </p>
            {issues.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Needs a look</h3>
                <ul className="max-h-64 divide-y overflow-y-auto rounded-lg border text-sm">
                  {issues.map((line) => (
                    <li key={line.line} className="space-y-0.5 px-3 py-2">
                      <div className="flex justify-between gap-3">
                        <span className="font-medium">
                          {line.manufacturer}
                          {line.promotion && <span className="font-normal"> · {line.promotion}</span>}
                        </span>
                        <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                          row {line.line}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {!line.manufacturer_found
                          ? 'Vendor not in the catalog'
                          : [
                              ...Object.entries(line.skipped).map(
                                ([reason, count]) => `${count.toLocaleString()} skipped: ${reason}`,
                              ),
                              ...(line.missing_models.length
                                ? [`Not in the catalog: ${line.missing_models.join(', ')}`]
                                : []),
                            ].join(' · ') || 'Matches no products'}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <LoadingButton
            disabled={!result}
            loading={importSpecials.isPending && result != null}
            loadingText="Loading..."
            onClick={apply}
          >
            Load list
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
